import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import LiabilityWaiverModal from './LiabilityWaiverModal';
import { Loader2 } from 'lucide-react';

/**
 * WaiverGuard component ensures that players moving into Cascadia Willow Club 
 * are forced to sign the Cascadia-specific liability waiver on their first login,
 * without forcing them through the entire PWA Registration Form process again.
 */
const WaiverGuard = ({ user, clubInfo, children }) => {
  const [checkingWaiver, setCheckingWaiver] = useState(true);
  const [needsWaiver, setNeedsWaiver] = useState(false);
  const [waiverContent, setWaiverContent] = useState(null);

  useEffect(() => {
    const checkWaiverStatus = async () => {
      // Fast bypass for non-logged in users or missing data
      if (!user || !user.uid || !clubInfo) {
        setCheckingWaiver(false);
        return;
      }

      // Check if the current club is Cascadia Willow Club
      const isCascadiaClub = clubInfo.name.toLowerCase().includes('cascadia') || clubInfo.name === 'CWC';
      
      const requiresWaiver = clubInfo.requireWaiver || isCascadiaClub;

      if (!requiresWaiver) {
        setCheckingWaiver(false);
        return;
      }
      
      // FIX: If the user is a brand new Cascadia player who hasn't completed the Registration form, 
      // DEFER to the RegistrationGuard. RegistrationGuard already forces the waiver inside its own form UI.
      if (user.registrationCompleted !== true) {
         console.log('✅ WaiverGuard: Deferring waiver to the native RegistrationGuard pipeline.');
         setCheckingWaiver(false);
         return;
      }

      try {
        console.log('🛡️ WaiverGuard: Checking waiver for Cascadia Willow Club');
        
        // Check if the user has ALREADY signed the required waiver
        const userWaiverRef = doc(db, 'userWaiverAgreements', user.uid);
        const docSnap = await getDoc(userWaiverRef);

        let waiverVersion = isCascadiaClub ? 'Cascadia_v1' : `ClubWaiver_${clubInfo.id}`;

        if (docSnap.exists() && docSnap.data().agreements) {
          // Look through their past agreements to see if they signed this club's waiver
          const agreements = docSnap.data().agreements;
          const hasSignature = agreements.some(agg => agg.version === waiverVersion);
          
          if (hasSignature) {
            console.log('✅ WaiverGuard: User has already signed waiver.');
            setCheckingWaiver(false);
            return;
          }
        }

        // User HAS NOT signed the required waiver. Need to display the Liability Form.
        const defaultContent = `In consideration of being part of ${clubInfo.name} and participating in activities of the club, I, the undersigned, acknowledge and agree as follows:

1. Assumption of Risk: I understand that participating in sports and recreational activities involves inherent risks, including but not limited to physical injury, mental stress, and financial loss. I voluntarily assume all such risks associated with my and my family's participation.

2. Release of Liability: I release ${clubInfo.name}, its officers, directors, employees, volunteers, and agents from any and all liability for any injuries, damages, or losses arising from my and/or my family's participation in the club's activities that are directly conducted by the club or where I and/or my family participate on behalf of, or as a player of, ${clubInfo.name}. This release includes claims related to negligence, breach of duty, or any other cause of action.

3. Responsibility for Personal Safety: I agree to take full responsibility for my own and/or my family's safety during activities organized by ${clubInfo.name} or where I and/or my family participate on behalf of, or as a player of, ${clubInfo.name}. I and my family will follow all safety guidelines, use appropriate protective gear, and exercise caution.

4. No Claims Against the Club: I waive any right to make any claims, demands, or legal actions against ${clubInfo.name}, its officers, directors, employees, volunteers, and agents - arising out of my and/or my family's participation in its activities or any activities where I and/or my family participate on behalf of, or as a player of, ${clubInfo.name}.

5. Binding Effect: This waiver and release shall be binding upon me, my heirs, executors, administrators, and assigns.`;

        const dynamicWaiver = {
          version: waiverVersion,
          effectiveDate: new Date().toISOString(),
          content: clubInfo.waiverText || defaultContent
        };

        setWaiverContent(dynamicWaiver);
        setNeedsWaiver(true);

      } catch (error) {
        console.error('❌ Error checking WaiverGuard status:', error);
      } finally {
        setCheckingWaiver(false);
      }
    };

    checkWaiverStatus();
  }, [user, clubInfo]);

  // Handle successful agreement
  const handleWaiverAgreed = () => {
    console.log('✅ WaiverGuard: Waiver successfully signed!');
    setNeedsWaiver(false);
  };

  // Loading Screen
  if (checkingWaiver) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <Loader2 className="animate-spin h-10 w-10 text-blue-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm font-medium">Verifying club requirements...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={needsWaiver ? 'filter blur-sm pointer-events-none select-none h-screen overflow-hidden' : ''}>
        {children}
      </div>
      
      {needsWaiver && waiverContent && (
        <LiabilityWaiverModal 
          waiver={waiverContent}
          user={user}
          clubName={clubInfo.name}
          onAgree={handleWaiverAgreed}
        />
      )}
    </>
  );
};

export default WaiverGuard;
